/**
 * Friend Stats Hit Rate Helper
 * Computes hit rate as picks correct / all picks (all picks across markets), not per-challenge or per-match.
 * Only finished matches contribute.
 */

import type { PickHistoryEntry } from '../types/friends.types';
import { MATCH_WINNER_MARKET_ID } from '../../challenges/constants/challenges.constants';
import { getMatchResult } from './friendStatsPrediction.helper';

const TERMINAL_MATCH_STATUSES = new Set(['FINISHED', 'CANCELLED', 'POSTPONED', 'ABANDONED']);

export interface HitRateStats {
  hits: number;
  total: number;
  percentage: number;
}

export interface BothHitRates {
  user: HitRateStats;
  friend: HitRateStats;
}

function computeHitRateForSide(
  pickHistory: PickHistoryEntry[],
  side: 'user' | 'friend'
): HitRateStats {
  let hits = 0;
  let total = 0;

  for (const entry of pickHistory) {
    for (const match of entry.matches) {
      const isFinished =
        TERMINAL_MATCH_STATUSES.has(match.matchResult.status) &&
        match.matchResult.homeScore !== null &&
        match.matchResult.awayScore !== null;
      if (!isFinished) continue;

      const matchResult = getMatchResult(
        match.matchResult.homeScore,
        match.matchResult.awayScore
      );

      if (side === 'user') {
        if (match.userPrediction?.outcome != null) {
          total += 1;
          const correct =
            (match.userPrediction.pointsEarned ?? 0) > 0 ||
            (match.userPrediction.pointsEarned == null &&
              matchResult !== null &&
              match.userPrediction.outcome === matchResult);
          if (correct) hits += 1;
        }
        const otherPicks = (match.userPicks ?? []).filter((p) => p.marketId !== MATCH_WINNER_MARKET_ID);
        for (const p of otherPicks) {
          total += 1;
          if ((p.pointsEarned ?? 0) > 0 || p.isCorrectOutcome === true) hits += 1;
        }
      } else {
        if (match.friendPrediction?.outcome != null) {
          total += 1;
          const correct =
            (match.friendPrediction.pointsEarned ?? 0) > 0 ||
            (match.friendPrediction.pointsEarned == null &&
              matchResult !== null &&
              match.friendPrediction.outcome === matchResult);
          if (correct) hits += 1;
        }
        const otherPicks = (match.friendPicks ?? []).filter((p) => p.marketId !== MATCH_WINNER_MARKET_ID);
        for (const p of otherPicks) {
          total += 1;
          if ((p.pointsEarned ?? 0) > 0 || p.isCorrectOutcome === true) hits += 1;
        }
      }
    }
  }

  const percentage = total > 0 ? Math.round((hits / total) * 100) : 0;
  return { hits, total, percentage };
}

/**
 * Compute hit rate for both user and friend from all challenges (ACTIVE + COMPLETED).
 * Only finished matches (with scores) contribute; per-match isFinished check applies.
 */
export function getHitRateFromPickHistory(pickHistory: PickHistoryEntry[]): BothHitRates {
  return {
    user: computeHitRateForSide(pickHistory, 'user'),
    friend: computeHitRateForSide(pickHistory, 'friend'),
  };
}
